import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaTrashAlt } from "react-icons/fa";
import useSortableData from "../../utils/SortData";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import {
  HeroiconsOutlineCalendar,
  TablerCaretUpDownFilled,
} from "../../assets/icons/LayoutIcon";
import { CircleMinus, CirclePlus, Minus, Plus } from "lucide-react";
import { FaChevronDown } from "react-icons/fa6";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loading from "../Layout/Loading";
import {
  getUserDetailsForApplicationSale,
  getDoctorsForApplicationSale,
  RetrieveItemGroups,
} from "../../services/applicationReport";
import { getUserId, getDistributorNames } from "../../Common/functions";
import TableSearchableDropdown from "../Layout/TableSearchableDropdown";
// import TableSearchableDropdown from "./TableSearchableDropdown"; // Import the table-optimized searchable dropdown

const defaultRow = {
  date: "",
  doctor_name: "",
  doctors_id: "",
  product_group: "",
  product_name: "",
  product_id: "",
  product_code: "",
  unit_price: "",
  quantity: 0,
};

const isRowComplete = (row) => {
  return (
    row.date.trim() !== "" &&
    row.product_group.trim() !== "" &&
    row.product_name.trim() !== "" &&
    row.doctor_name.trim() !== "" &&
    parseInt(row.quantity) > 0
  );
};

const isRowEmpty = (row) => {
  return (
    !row.date &&
    !row.doctor_name &&
    !row.product_group &&
    !row.product_name &&
    row.quantity === 0
  );
};

const SUBMISSION_COOLDOWN = 15000;

const AddMultiEntry = ({ handleBack, userRole }) => {
  const [rows, setRows] = useState([{ ...defaultRow }]);
  const [loading, setLoading] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(0);

  // Data states
  const [userProfileDetails, setUserProfileDetails] = useState({});
  const [productGroupList, setProductGroupList] = useState([]);
  const [doctorsNameList, setDoctorsNameList] = useState([]);
  const [productNameOptions, setProductNameOptions] = useState({});

  // Get user profile details
  const getUserProfileDetails = async () => {
    try {
      const result = await getUserDetailsForApplicationSale({
        category:
          userRole === "super_admin" || userRole === "application_specialist"
            ? "user-profile"
            : userRole === "distributor_admin"
            ? "distributor-admin-profile"
            : "Internal-user-profile",
        UserId: getUserId(),
      });
      setUserProfileDetails(result?.data[0] || {});
      console.log("setUserProfileDetails", result?.data[0]);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  // Get product groups
  const getProductGroup = async () => {
    try {
      const result = await RetrieveItemGroups(1747653662341);
      const groups = result?.data?.text?.itemgroups || [];
      setProductGroupList(groups);
    } catch (error) {
      console.error("Error fetching product groups:", error);
    }
  };

  // Get doctors list
  const getDoctors = async () => {
    try {
      const result = await getDoctorsForApplicationSale({
        Distributor_Name: getDistributorNames()[0],
      });
      setDoctorsNameList(result?.data?.records || []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  useEffect(() => {
    getUserProfileDetails();
    getProductGroup();
    getDoctors();
  }, []);

  // Generate timestamp for unique identification
  const generateTimestamp = () => {
    const date = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleInputChange = (index, key, value) => {
    const updatedRows = [...rows];

    if (key === "product_group") {
      const selectedGroup = productGroupList.find(
        (group) => group.group_name === value
      );
      updatedRows[index] = {
        ...updatedRows[index],
        product_group: value,
        product_name: "",
        product_id: "",
        product_code: "",
        unit_price: "",
      };

      // Store product options for this group
      if (selectedGroup) {
        setProductNameOptions((prev) => ({
          ...prev,
          [index]: selectedGroup.items || [],
        }));
      }
    } else if (key === "product_name") {
      const productOptions = productNameOptions[index] || [];
      const selectedProduct = productOptions.find(
        (item) => item.name === value
      );
      updatedRows[index] = {
        ...updatedRows[index],
        product_name: value,
        product_id: selectedProduct ? selectedProduct.item_id : "",
        product_code: selectedProduct ? selectedProduct.name : "",
        unit_price: selectedProduct ? selectedProduct.rate : "",
      };
    } else if (key === "doctor_name") {
      const selectedDoctor = doctorsNameList.find(
        (doctor) => doctor.Full_Name === value
      );
      updatedRows[index] = {
        ...updatedRows[index],
        doctor_name: value,
        doctors_id: selectedDoctor ? selectedDoctor.id : "",
      };
    } else {
      updatedRows[index][key] = value;
    }

    // Check if the current row is filled, and if it is, add a new empty row
    if (index === updatedRows.length - 1 && isRowComplete(updatedRows[index])) {
      updatedRows.push({ ...defaultRow });
    }

    setRows(updatedRows);
  };

  const handleAddRow = (index) => {
    if (isRowComplete(rows[index])) {
      const updatedRows = [...rows];
      updatedRows.splice(index + 1, 0, { ...defaultRow });
      setRows(updatedRows);
    } else {
      toast.error("Please fill out the current row before adding a new one.");
    }
  };

  const handleRemoveRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);

    // Remove corresponding product options
    const newProductOptions = { ...productNameOptions };
    delete newProductOptions[index];
    setProductNameOptions(newProductOptions);

    setRows(updatedRows.length ? updatedRows : [{ ...defaultRow }]);
  };

  // Create CRM record
  const createCRMRecord = async (data) => {
    const crmFormat = JSON.stringify({ data: [data] });
    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/Create_records_custom_module_zoho_crm",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: crmFormat,
        }
      );
      const result = await response.json();
      const parsedText = JSON.parse(result.text);
      return parsedText.data[0].details.id;
    } catch (error) {
      console.error("Error creating CRM record:", error);
      throw error;
    }
  };

  // Create sales record
  const createSalesRecord = async (data) => {
    const crmFormat = JSON.stringify({ data: [data] });
    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/crm/Create_records_sales_module_zoho_crm",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: crmFormat,
        }
      );
      const result = await response.json();
      const parsedText = JSON.parse(result.text);
      return parsedText.data[0].details.id;
    } catch (error) {
      console.error("Error creating sales record:", error);
      throw error;
    }
  };

  // Submit to sales tracking
  const submitToSalesTracking = async (dataArray) => {
    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/sales_tracking/Avana_create_multiple_sales",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(dataArray),
        }
      );
      return await response.json();
    } catch (error) {
      console.error("Error submitting to sales tracking:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    // Check submission cooldown
    const currentTime = new Date().getTime();
    if (currentTime - lastSubmitted < SUBMISSION_COOLDOWN) {
      toast.warning("Please wait before submitting again");
      return;
    }

    const validRows = rows.filter(
      (row) => !isRowEmpty(row) && isRowComplete(row)
    );

    if (validRows.length === 0) {
      toast.error("Please add at least one complete row");
      return;
    }

    setLoading(true);
    setLastSubmitted(currentTime);

    try {
      const processedData = [];

      for (const row of validRows) {
        const timestampUnique = generateTimestamp();

        const data = {
          quantity: row.quantity,
          product_id: row.product_id,
          product_group: row.product_group,
          product_name: row.product_name,
          product_code: row.product_code,
          patientnumber: " ",
          patientname: " ",
          patientemail: " ",
          sales_order_status: "Pending",
          tracking_status: "Sale",
          UserId: getUserId(),
          username: userProfileDetails.username || "",
          nickname: userProfileDetails.nickname || "",
          timestamp: "",
          group_title: userProfileDetails.GroupName || "",
          team: userProfileDetails.team || "",
          assigner: userProfileDetails.assignername || "",
          managerID: "manager_id_here",
          distributor_name: getDistributorNames()[0],
          doctorsname: row.doctor_name,
          doctors_name: row.doctors_id,
          dates: row.date,
          unit_price: row.unit_price,
          category: "sales tracking",
          Name: timestampUnique,
        };

        // Create sales record first
        const salesRecordId = await createSalesRecord(data);
        console.log("Sales Record ID:", salesRecordId);

        // Create CRM record
        const recordId = await createCRMRecord(data);
        console.log("CRM Record ID:", recordId);

        // Add IDs to data
        data.sales_record_id = salesRecordId;
        data.record_id = recordId;

        processedData.push(data);
      }

      // Submit all to sales tracking
      const salesTrackingResponse = await submitToSalesTracking(processedData);
      console.log("Sales tracking response:", salesTrackingResponse);

      toast.success(`Successfully added ${processedData.length} sales records`);

      // Reset form
      setRows([{ ...defaultRow }]);
      setProductNameOptions({});

      // Optionally go back or redirect
      setTimeout(() => {
        handleBack();
      }, 1500);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Error occurred during submission");
    } finally {
      setLoading(false);
    }
  };

  const tableHeaders = [
    { label: "Add/Remove", value: "action" },
    { label: "Date", value: "date" },
    { label: "Doctor", value: "doctor_name" },
    { label: "Product Group", value: "product_group" },
    { label: "Product Name", value: "product_name" },
    { label: "Quantity", value: "quantity" },
  ];

  const { sortedItems, handleSort } = useSortableData(rows);

  const incrementQuantity = (index) => {
    const updated = [...rows];
    const currentValue = parseInt(updated[index].quantity) || 0;
    updated[index].quantity = currentValue + 1;
    setRows(updated);
  };

  const decrementQuantity = (index) => {
    const updated = [...rows];
    const currentValue = parseInt(updated[index].quantity) || 0;
    updated[index].quantity = Math.max(0, currentValue - 1);
    setRows(updated);
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="sm:px-4 px-0 py-4">
      <style>
        {`
          @media (max-width: 576px) {
            .ResponsiveTable tr {
              display: flex;
              flex-direction: column;
              align-items: stretch;
              border-bottom: 1px solid #e5e7eb;
              padding: 0;
            }
            .ResponsiveTable td {
              display: flex;
              align-items: center;
              padding: 8px 16px;
              margin: 0;
              border: none;
              width: 100%;
              box-sizing: border-box;
            }
            .ResponsiveTable td:before {
              content: attr(data-label);
              font-weight: bold;
              margin-right: -120px;
              flex-shrink: 0;
              width: 110px;
            }
            .ResponsiveTable td .relative,
            .ResponsiveTable td .flex {
              width: 100%;
            }
            .ResponsiveTable td input,
            .ResponsiveTable td .flex input {
              flex-grow: 1;
            }
          }
        `}
      </style>
      <div className="flex justify-between items-center gap-6">
        <div className="flex justify-start items-center gap-2">
          <button onClick={handleBack}>
            <FaChevronLeft className="text-text1 w-5 h-5" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-text1">
            Create Multiple Sales Entries
          </h1>
        </div>
        <button
          onClick={handleSubmit}
          className="text-white text-sm bg-primary px-3 py-2 rounded-md"
          disabled={loading}
        >
          Submit (
          {rows.filter((row) => !isRowEmpty(row) && isRowComplete(row)).length})
        </button>
      </div>

      <hr className="mt-6" />

      <div className="mt-10">
        <div className="rounded-lg sm:border border-gray-300 overflow-x-auto my-10">
          <Table className="w-full border-gray-300 rounded-lg ResponsiveTable">
            <Thead className="bg-gray-100 sm:border-b rounded-lg">
              <Tr>
                {tableHeaders.map((header, index) => (
                  <Th
                    key={index}
                    className="px-4 py-3 text-left font-semibold text-xs text-text1 whitespace-nowrap"
                  >
                    {header.value !== "action" ? (
                      <div
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={() => handleSort(header.value)}
                      >
                        <TablerCaretUpDownFilled className="hidden sm:block" />
                        <span>{header.label}</span>
                      </div>
                    ) : (
                      <span>{header.label}</span>
                    )}
                  </Th>
                ))}
              </Tr>
            </Thead>

            <Tbody>
              {sortedItems.map((item, index) => (
                <Tr key={index} className="border-t">
                  <Td className="px-4 py-4 text-xs text-text2">
                    {index === rows.length - 1 ? (
                      <button
                        onClick={() => handleAddRow(index)}
                        className="flex items-center justify-center w-10 h-10"
                      >
                        <CirclePlus className="text-gray-600" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRemoveRow(index)}
                        className="flex items-center justify-center w-10 h-10"
                      >
                        <CircleMinus className="text-gray-400" />
                      </button>
                    )}
                  </Td>
                  <Td className="px-3 py-4 text-xs text-text2">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 lg:pl-3 flex items-center pointer-events-none">
                        <HeroiconsOutlineCalendar className="w-4 h-4 text-gray-500" />
                      </div>
                      <input
                        type="date"
                        value={item.date}
                        placeholder="mm/dd/yyyy"
                        onChange={(e) =>
                          handleInputChange(index, "date", e.target.value)
                        }
                        max={getTodayDate()}
                        className="w-full custom-dateinput  rounded-md border-gray-300 focus:outline-none focus:ring-0  appearance-none date-input-no-indicator ml-4 lg:ml-5"
                      />
                    </div>
                  </Td>

                  {/* Doctor Name - Now Searchable */}
                  <Td className="px-3 py-4 text-xs text-text2">
                    <TableSearchableDropdown
                      options={doctorsNameList}
                      value={item.doctor_name}
                      onChange={handleInputChange}
                      name="doctor_name"
                      placeholder="Select Doctor"
                      searchPlaceholder="Search doctors..."
                      optionKey="Full_Name"
                      optionLabel="Full_Name"
                      noOptionsMessage="No doctors found"
                      index={index}
                    />
                  </Td>

                  {/* Product Group - Now Searchable */}
                  <Td className="px-3 py-4 text-xs text-text2">
                    <TableSearchableDropdown
                      options={productGroupList}
                      value={item.product_group}
                      onChange={handleInputChange}
                      name="product_group"
                      placeholder="Select Product Group"
                      searchPlaceholder="Search product groups..."
                      optionKey="group_name"
                      optionLabel="group_name"
                      noOptionsMessage="No product groups found"
                      index={index}
                    />
                  </Td>

                  {/* Product Name - Now Searchable */}
                  <Td className="px-3 py-4 text-xs text-text2">
                    <TableSearchableDropdown
                      options={productNameOptions[index] || []}
                      value={item.product_name}
                      onChange={handleInputChange}
                      name="product_name"
                      placeholder="Select Product"
                      searchPlaceholder="Search products..."
                      optionKey="name"
                      optionLabel="name"
                      noOptionsMessage="No products found"
                      disabled={!item.product_group}
                      index={index}
                    />
                  </Td>

                  <Td className="px-3 py-4 text-xs text-text2">
                    <div className="flex items-center">
                      <button
                        onClick={() => decrementQuantity(index)}
                        className="border-gray-300 rounded-l p-1 w-8 flex justify-center"
                      >
                        <Minus size={16} strokeWidth={2} />
                      </button>
                      <input
                        type="text"
                        value={item.quantity}
                        onChange={(e) => {
                          const onlyNums = e.target.value.replace(/\D/g, "");
                          if (onlyNums.length <= 10) {
                            handleInputChange(index, "quantity", onlyNums);
                          }
                        }}
                        className=" border-gray-300 py-1 px-2 text-center w-10 text-xs"
                      />
                      <button
                        onClick={() => incrementQuantity(index)}
                        className=" border-gray-300 rounded-r p-1 w-8 flex justify-center"
                      >
                        <Plus size={16} strokeWidth={2} />
                      </button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default AddMultiEntry;
